/**
 * ModalButtonGroup Component
 * Reusable button group for modal actions (Cancel + Submit/Confirm)
 *
 * Features:
 * - Consistent button styling across all modals
 * - Support for primary, danger, and warning variants
 * - Loading state handling
 * - Flexible layout (flex or fixed width)
 * - Customizable button labels
 * - Disabled state management
 */

import {
  PRIMARY_BUTTON_STYLE,
  SECONDARY_BUTTON_STYLE,
  DANGER_BUTTON_STYLE,
  WARNING_BUTTON_STYLE,
  BUTTON_GROUP_STYLE,
} from '../../styles/modalStyles';

export type ButtonVariant = 'primary' | 'danger' | 'warning';

export interface ModalButtonGroupProps {
  onCancel: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  submitType?: 'button' | 'submit';
  isLoading?: boolean;
  loadingLabel?: string;
  submitVariant?: ButtonVariant;
  flex?: boolean;
  disabled?: boolean;
  hideCancel?: boolean;
}

const BUTTON_VARIANTS = {
  primary: PRIMARY_BUTTON_STYLE,
  danger: DANGER_BUTTON_STYLE,
  warning: WARNING_BUTTON_STYLE,
} as const;

export function ModalButtonGroup({
  onCancel,
  onSubmit,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  submitType = 'submit',
  isLoading = false,
  loadingLabel,
  submitVariant = 'primary',
  flex = false,
  disabled = false,
  hideCancel = false,
}: ModalButtonGroupProps) {
  const submitButtonStyle = BUTTON_VARIANTS[submitVariant];
  const isSubmitDisabled = isLoading || disabled;

  return (
    <div style={BUTTON_GROUP_STYLE}>
      {!hideCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          style={{
            ...SECONDARY_BUTTON_STYLE,
            ...(flex ? { flex: 1 } : {}),
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {cancelLabel}
        </button>
      )}
      <button
        type={submitType}
        onClick={onSubmit}
        disabled={isSubmitDisabled}
        style={{
          ...submitButtonStyle,
          ...(flex ? { flex: 1 } : {}),
          cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
          opacity: isSubmitDisabled ? 0.6 : 1,
          backgroundColor: isLoading
            ? '#9ca3af'
            : submitButtonStyle.backgroundColor,
        }}
      >
        {isLoading ? (loadingLabel || 'Loading...') : submitLabel}
      </button>
    </div>
  );
}
